import { ReactNode, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { getRelativeTimeAgo } from '@lib/date/getRelativeTimeAgo';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { useUserInfo } from '@domain/user/useUserInfo';
import { getUserUrl } from '@domain/routes/urls';
import { Avatar, AvatarSize } from '../../../Avatar/Avatar';
import { Button } from '../../../Clickables/Buttons/Button';
import { InternalLink } from '../../../Clickables/InternalLink/InternalLink';
import { CollapsibleText } from '../../../CollapsibleText/CollapsibleText';
import { Heading } from '../../../Heading/Heading';
import { LikeButton } from '../../../LikeButton/LikeButton';
import { TagContent } from '../../../TagContent/TagContent';
import { Text } from '../../../Text/Text';
import type { PostComments } from '../../types/PostComments';

interface PostCommentProps {
  avatarUrl: string;
  children: ReactNode;
  className?: string;
  date: Date;
  likeCount: number;
  likedByCurrentUser: boolean;
  nickname: string;
  onNotLoggedIn: () => void;
  onLikesButtonClick: () => void;
  replies?: PostComments;
}

export const PostComment = ({
  avatarUrl,
  children,
  className,
  date,
  likeCount,
  likedByCurrentUser,
  nickname,
  onNotLoggedIn: handleNotLoggedIn,
  onLikesButtonClick,
  replies,
}: PostCommentProps) => {
  const timePassageAbbreviation = getRelativeTimeAgo(date);
  const repliesList = getNodesFromConnection(replies);
  const [showComments, setShowComments] = useState(false);
  const { isLoggedIn } = useUserInfo();
  const { currentPage } = useAnalytics();

  const toggleCommentReplies = (): void => {
    if (!isLoggedIn) {
      handleNotLoggedIn();
      return;
    }
    setShowComments(!showComments);
  };

  const handleLikesButtonClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'read-more',
        source: 'post-viewer-like',
        page: currentPage,
      },
    });
    onLikesButtonClick();
  };

  return (
    <div data-testid="PostComment">
      <div className={twMerge('flex gap-x-3', className)}>
        <Avatar alt={`${nickname} avatar`} src={avatarUrl} size={AvatarSize.CATCH_SMALL} />
        <section className="flex w-full [scrollbar-width:'none'] flex-col flex-nowrap gap-y-1.5 overflow-x-scroll [-ms-overflow-style:'none'] [&::-webkit-scrollbar]:hidden">
          <Text className="text-trench m-0" constant variant="sm">
            <InternalLink variant="textPrimary" to={getUserUrl(nickname)}>
              <Heading as="span" className="" variant="3xs">
                {nickname}
              </Heading>{' '}
            </InternalLink>
            <CollapsibleText as="span" maxLines={2}>
              {children}
            </CollapsibleText>
          </Text>

          <Text className="text-trench m-0" variant="sm">
            {timePassageAbbreviation}
            {likeCount === 1 && <span className="ml-6">{likeCount} like</span>}
            {likeCount > 1 && <span className="ml-6">{likeCount} likes</span>}
          </Text>
        </section>
        <LikeButton
          className="text-abyss h-full shrink-0 cursor-pointer"
          hideNumber
          iconSize={14}
          isLiked={likedByCurrentUser}
          numberOfLikes={likeCount || 0}
          onLikesButtonClick={handleLikesButtonClick}
          type="post"
        />
      </div>
      {replies && replies.totalCount > 0 && (
        <>
          <Button
            variant="textNone"
            data-testid="toggle-comment-replies-button"
            onClick={toggleCommentReplies}
            onKeyDown={toggleCommentReplies}
            role="button"
            tabIndex={0}
            className="inline-flex w-full items-center gap-x-3"
          >
            <hr className="border-midnight w-[43px]" />
            <Text className="text-midnight m-0" constant variant="sm">
              {showComments ? 'Hide' : 'View'} {replies.totalCount} repl
              {replies.totalCount === 1 ? 'y' : 'ies'}
            </Text>
          </Button>
          {showComments && (
            <div className="ml-[55px] flex flex-col gap-y-4">
              {repliesList.map(
                ({
                  _id: replyPostId,
                  createdAt: replyDate,
                  likedByCurrentUser: replyLikedByCurrentUser,
                  likesCount,
                  text: replyText,
                  user: replyUser,
                }) => {
                  return (
                    replyText.text && (
                      <PostComment
                        key={replyPostId}
                        avatarUrl={replyUser.avatar?.url ?? ''}
                        date={new Date(replyDate)}
                        likeCount={likesCount}
                        likedByCurrentUser={replyLikedByCurrentUser}
                        nickname={replyUser.nickname || ''}
                        onLikesButtonClick={handleLikesButtonClick}
                        onNotLoggedIn={handleNotLoggedIn}
                      >
                        <TagContent id={replyUser._id} text={replyText.text} url="/tags/" />
                      </PostComment>
                    )
                  );
                },
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
